import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, User, Trophy, Users, Calendar, Loader2, Mail, Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';

export default function GlobalSearch({ onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults(null);
      return;
    }

    // Debounce search
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setResults(response.data);
        setIsOpen(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query]);

  const handleClose = () => {
    setQuery('');
    setResults(null);
    setIsOpen(false);
    if (onClose) onClose();
  };

  const ResultSection = ({ title, icon: Icon, items, type }) => {
    if (!items || items.length === 0) return null;

    return (
      <div className="mb-4">
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-sm font-semibold text-gray-600">
          <Icon className="w-4 h-4" />
          <span>{title}</span>
          <Badge variant="secondary" className="ml-auto text-xs">{items.length}</Badge>
        </div>
        <div className="divide-y">
          {items.map((item, idx) => (
            <Link
              key={idx}
              to={getItemLink(type, item)}
              onClick={handleClose}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[#C5D5D5]/20 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-[#2D5A5A]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#2D5A5A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {getItemTitle(type, item)}
                </div>
                <div className="text-sm text-gray-500 truncate">
                  {getItemSubtitle(type, item)}
                </div>
              </div>
              {type === 'player' && (
                <div className="flex flex-col items-end text-xs text-gray-400">
                  {item.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {item.email.substring(0, 20)}...
                    </span>
                  )}
                  {item.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {item.phone}
                    </span>
                  )}
                </div>
              )}
              {type === 'match' && (
                <Badge className={`${
                  item.status === 'live' ? 'bg-red-500' :
                  item.status === 'completed' ? 'bg-green-500' : 'bg-[#5D8A8A]'
                } text-white border-none text-xs`}>
                  {item.status?.toUpperCase()}
                </Badge>
              )}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  const getItemLink = (type, item) => {
    switch (type) {
      case 'player': return `/players/${item.player_id}`;
      case 'match': return `/matches/${item.match_id}`;
      case 'league': return `/leagues/${item.league_id}`;
      case 'team': return `/teams/${item.team_id}`;
      default: return '#';
    }
  };

  const getItemTitle = (type, item) => {
    switch (type) {
      case 'player': return item.name;
      case 'match': return `${item.team_a?.short_name || item.team_a?.name || 'TBA'} vs ${item.team_b?.short_name || item.team_b?.name || 'TBA'}`;
      case 'league': return item.name;
      case 'team': return item.name;
      default: return 'Unknown';
    }
  };

  const getItemSubtitle = (type, item) => {
    switch (type) {
      case 'player': return `${item.team?.name || 'Unknown Team'} • ${item.player_role || 'Player'}`;
      case 'match': return `${item.league?.name || ''} • ${item.ground || ''}`;
      case 'league': return `${item.location || ''} • ${item.season || ''}`;
      case 'team': return `${item.league?.name || ''} • ${item.short_name || ''}`;
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={handleClose}>
      <div 
        className="absolute top-0 left-0 right-0 bg-white shadow-2xl max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b bg-white sticky top-0">
          <Search className="w-5 h-5 text-gray-400" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search players, matches, leagues, teams..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 border-0 focus-visible:ring-0 text-lg placeholder:text-gray-400"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="w-5 h-5 text-[#2D5A5A] animate-spin" />}
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Results */}
        <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
          {query.length < 2 && (
            <div className="p-8 text-center text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Type at least 2 characters to search</p>
              <p className="text-sm mt-2">Search by player name, email, phone, team, league, or match</p>
            </div>
          )}

          {query.length >= 2 && !loading && results && results.total === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No results found for "{query}"</p>
              <p className="text-sm mt-2">Try searching by different keywords</p>
            </div>
          )}

          {results && results.total > 0 && (
            <>
              <ResultSection title="Players" icon={User} items={results.players} type="player" />
              <ResultSection title="Teams" icon={Users} items={results.teams} type="team" />
              <ResultSection title="Leagues" icon={Trophy} items={results.leagues} type="league" />
              <ResultSection title="Matches" icon={Calendar} items={results.matches} type="match" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
